import { TFireEvent } from "@/types/common";
import { v4 as uuidv4 } from "uuid";
import crypto, { CipherKey } from "crypto";
import Moji from "moji";
import { FormEvent } from "react";

/**
 * Generates a new UUID (Universally Unique Identifier).
 *
 * @returns {string} - The generated UUID.
 */
export const makeUuid = (): string => {
  return uuidv4();
};

/**
 * Converts a hex color code to an RGBA color string.
 *
 * @param {string} hexColor - The hex color code.
 * @param {number} alpha - The alpha (transparency) value (0 to 1).
 * @returns {string} - The RGBA color string.
 */
export const hexToRgba = (hexColor: string, alpha: number): string => {
  hexColor = hexColor.replace("#", "");
  // Convert hexadecimal to RGB
  const red = parseInt(hexColor.substring(0, 2), 16);
  const green = parseInt(hexColor.substring(2, 4), 16);
  const blue = parseInt(hexColor.substring(4, 6), 16);

  // Validate alpha value
  alpha = Math.min(1, Math.max(0, alpha));

  // Format and return RGBA string
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

/**
 * Removes duplicate elements from an array.
 *
 * @template T
 * @param {T[]} arr - The array to process.
 * @returns {T[]} - A new array with unique elements.
 */
export const uniqueArray = <T>(arr: T[]): T[] => {
  return Array.from(new Set(arr));
};

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} ms - The number of milliseconds to sleep.
 * @returns {Promise<void>} - A promise that resolves after the specified time.
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const isFireEvent = <T>(event: Event): event is TFireEvent<T> => {
  if ("detail" in event) return true;
  return false;
};

export const decrypt = (encryptedText: string, secretKey: string) => {
  const [iv, encrypted] = encryptedText.split(":");
  if (!iv || !encrypted) return encryptedText;
  if (iv.length < 16) return encryptedText;
  if (encrypted.length < 32) return encryptedText;
  const secret = Buffer.from(secretKey, "utf-8").slice(-32);
  const decipher = crypto.createDecipheriv("aes-256-cbc", secret as unknown as CipherKey, Buffer.from(iv, "hex") as unknown as Uint8Array);
  let decrypted = decipher.update(encrypted, "hex", "utf8");
  decrypted += decipher.final("utf8");
  return decrypted;
};

export const convertHalfWidthToFullWidth = (text: string): string => {
  if (!text) return text;

  const fullWidth = Moji(text)
    .convert("HE", "ZE") // alphanumeric (a-z, 0-9) to full-width
    .convert("HK", "ZK") // half-width Katakana to full-width Katakana
    .toString()
    .replace(/ /g, "　");

  return fullWidth;
};

export const convertFullWidthToHalfWidth = (text: string): string => {
  if (!text) return text;

  const halfWidth = Moji(text)
    .convert("ZE", "HE") // full-width alphanumeric (A-Z, 0-9) to half-width
    .convert("ZK", "HK") // full-width Katakana to half-width Katakana
    .toString()
    .replace(/　/g, " ");

  return halfWidth;
};

/**
 * Capture-phase input handler that converts full-width characters typed into an
 * INPUT element to their half-width equivalents in place, while preserving the
 * caret/selection position.
 *
 * Uses the native HTMLInputElement `value` setter so React's synthetic event
 * system still observes the change and downstream `onChange` handlers fire with
 * the converted value.
 *
 * @param {FormEvent<HTMLElement>} e - The input form event (attach via `onInputCapture`).
 * @returns {void}
 */
export const handleFullwidthInputCapture = (e: FormEvent<HTMLElement>): void => {
  const target = e.target as HTMLInputElement | null;
  if (!target || target.tagName !== "INPUT") return;
  const current = target.value;
  const converted = convertFullWidthToHalfWidth(current);
  if (converted === current) return;
  const selectionStart = target.selectionStart;
  const selectionEnd = target.selectionEnd;
  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  if (nativeSetter) nativeSetter.call(target, converted);
  else target.value = converted;
  if (selectionStart !== null && selectionEnd !== null) {
    try {
      target.setSelectionRange(selectionStart, selectionEnd);
    } catch {
      /* selection not supported on this input type */
    }
  }
};
